import React, { useCallback, useEffect } from 'react';
import { useQueryLoader } from 'react-relay';
import AppPermissionsCheck from '@atlassian/jira-app-permissions-check/src/AppPermissionsCheck.tsx';
import type { CustomHeaderProps } from '@atlassian/jira-issue-navigator/src/common/types.tsx';
import IssueNavigator, {
	type Props as IssueNavigatorProps,
} from '@atlassian/jira-issue-navigator/src/main.tsx';
import { JsdGettingStartedPanelShouldRender } from '@atlassian/jira-servicedesk-getting-started-panel/src/async.tsx';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { ProjectIssueNavigatorHeaderSkeleton } from '@atlassian/jira-skeletons/src/ui/issue-navigator/components/project-header.tsx';
import PROJECT_NAME_QUERY_AGG, {
	type documentTitleQuery,
} from '@atlassian/jira-relay/src/__generated__/documentTitleQuery.graphql.ts';

import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src/DocumentTitle.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import Header from './ui/main.tsx';
import NavigatorDocumentTitle from './ui/document-title/index.tsx';
import PageActions from './ui/page-actions/index.tsx';

type InnerProps = {
	CustomHeader: IssueNavigatorProps['CustomHeader'];
	HeaderSkeletonImage: IssueNavigatorProps['HeaderSkeletonImage'];
};

export type Props = Omit<IssueNavigatorProps, keyof InnerProps> & {
	projectKey: ProjectKey;
	showNav3Header?: boolean;
	/**
	 * This prop is used to determine if the hide done feature is enabled
	 */
	isHideDoneSupportEnabled?: boolean;
	/**
	 * This prop is used to determine if the grid navigation feature is enabled
	 */
	isGridNavigationEnabled?: boolean;
};

const ActionMenu = (customHeaderProps: CustomHeaderProps) => {
	const {
		jql,
		filterId,
		filterDetails,
		searchResultPageData,
		HierarchyToggle,
		GroupByDropdown,
		HideDoneToggle,
		FiltersMenu,
		SaveResetViewSettingsButton,
		isHideDoneWorkItemsEnabled,
		isGridNavigationEnabled,
		isConditionalFormattingEnabled,
		isInlineFieldConfigEnabled,
		isModifiedFilter,
		onChangeFilter,
		onChangeJqlAndGroupBy,
	} = customHeaderProps;
	return (
		<PageActions
			jql={jql}
			filterId={filterId}
			isModifiedFilter={
				fg('visual-refresh-headers-buttons') ? isModifiedFilter : filterDetails?.jql !== jql
			}
			searchResultPageData={searchResultPageData}
			HierarchyToggle={HierarchyToggle}
			GroupByDropdown={GroupByDropdown}
			HideDoneToggle={HideDoneToggle}
			FiltersMenu={FiltersMenu}
			SaveResetViewSettingsButton={SaveResetViewSettingsButton}
			isHideDoneWorkItemsEnabled={isHideDoneWorkItemsEnabled}
			isConditionalFormattingEnabled={
				fg('jira_list_conditional_formatting') ? isConditionalFormattingEnabled : undefined
			}
			isGridNavigationEnabled={isGridNavigationEnabled}
			isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
			onChangeFilter={onChangeFilter}
			onChangeJqlAndGroupBy={onChangeJqlAndGroupBy}
		/>
	);
};

const actionMenuWithHideDoneSupport = (isHideDoneSupportEnabled?: boolean) => {
	return (customHeaderProps: CustomHeaderProps) => {
		return (
			<PageActions {...customHeaderProps} isHideDoneSupportEnabled={isHideDoneSupportEnabled} />
		);
	};
};

const ProjectIssueNavigator = ({
	projectKey,
	showNav3Header = false,
	isIssueHierarchySupportEnabled,
	isHideDoneSupportEnabled,
	isIssueRankingSupportEnabled,
	isMergedColumnSupportEnabled,
	isGridNavigationEnabled,
	isFilterRefinementEnabled,
	...rest
}: Props) => {
	const CustomHeader = useCallback(
		(customHeaderProps: CustomHeaderProps) => (
			<Header
				projectKey={projectKey}
				{...customHeaderProps}
				isQuickFiltersDisabled={showNav3Header}
				isHideDoneSupportEnabled={isHideDoneSupportEnabled}
				isIssueHierarchySupportEnabled={isIssueHierarchySupportEnabled}
			/>
		),
		[isHideDoneSupportEnabled, isIssueHierarchySupportEnabled, projectKey, showNav3Header],
	);

	const showNewNav = !showNav3Header;

	const [queryReference, loadQuery] = useQueryLoader<documentTitleQuery>(PROJECT_NAME_QUERY_AGG);
	const cloudId = useCloudId();

	useEffect(() => {
		loadQuery({ projectKey, cloudId });
	}, [loadQuery, projectKey, cloudId]);

	return (
		<>
			{showNewNav &&
				(queryReference ? (
					<NavigatorDocumentTitle queryReference={queryReference} />
				) : (
					// eslint-disable-next-line @atlassian/a11y/document-title -- TODO: All pages must have a document title. See go/a11y-document-title for more details.
					<DocumentTitle title="" />
				))}
			<IssueNavigator
				{...(!showNewNav && { CustomHeader })}
				{...(showNewNav && {
					ActionMenu: fg('jsc_bulk_edit_hide_done_work_items')
						? actionMenuWithHideDoneSupport(isHideDoneSupportEnabled)
						: ActionMenu,
				})}
				HeaderSkeletonImage={ProjectIssueNavigatorHeaderSkeleton}
				isIssueHierarchySupportEnabled={isIssueHierarchySupportEnabled}
				isIssueRankingSupportEnabled={isIssueRankingSupportEnabled}
				isGridNavigationEnabled={isGridNavigationEnabled}
				isMergedColumnSupportEnabled={isMergedColumnSupportEnabled}
				isFilterRefinementEnabled={isFilterRefinementEnabled}
				{...rest}
			/>
			<AppPermissionsCheck hasServiceDeskAccess>
				<JsdGettingStartedPanelShouldRender />
			</AppPermissionsCheck>
		</>
	);
};

type ProjectIssueNavigatorLoadedProps = {
	projectKey: ProjectKey;
	showNewNav: boolean;
};

export const ProjectIssueNavigatorLoaded = ({
	projectKey,
	showNewNav,
}: ProjectIssueNavigatorLoadedProps) => {
	const [queryReference, loadQuery] = useQueryLoader<documentTitleQuery>(PROJECT_NAME_QUERY_AGG);
	const cloudId = useCloudId();

	useEffect(() => {
		loadQuery({ projectKey, cloudId });
	}, [loadQuery, projectKey, cloudId]);

	return (
		<>
			{showNewNav &&
				(queryReference ? (
					<NavigatorDocumentTitle queryReference={queryReference} />
				) : (
					// eslint-disable-next-line @atlassian/a11y/document-title -- TODO: All pages must have a document title. See go/a11y-document-title for more details.
					<DocumentTitle title="" />
				))}
			<AppPermissionsCheck hasServiceDeskAccess>
				<JsdGettingStartedPanelShouldRender />
			</AppPermissionsCheck>
		</>
	);
};

export default ProjectIssueNavigator;
